import { Controller } from "@hotwired/stimulus"

/**
 * Polygon region editor — interactive SVG overlay for drawing freeform
 * mask regions on a scene thumbnail.
 *
 * UX flow:
 *   1. Click on the image to place vertices (numbered dots).
 *   2. Lines connect each vertex as you go.
 *   3. Semi-transparent fill previews the masked area.
 *   4. Click the first dot again (or hit "Close") to finish.
 *   5. After closing: drag any dot to adjust; click a midpoint to insert
 *      a new vertex; double-click a dot to remove it.
 *
 * Data:
 *   - Points are stored as normalised [0..1] JSON in a hidden input.
 *   - On change, the hidden input is updated and a `change` event is
 *     dispatched so parent forms can react.
 *
 * Usage in ERB:
 *   <div data-controller="polygon-region"
 *        data-polygon-region-points-value='[{"x":0.25,"y":0.1},...]'>
 *     <img data-polygon-region-target="image" src="..." />
 *     <input data-polygon-region-target="input" type="hidden" name="..." />
 *     <svg data-polygon-region-target="svg"></svg>
 *     <button data-action="polygon-region#close">Close polygon</button>
 *     <button data-action="polygon-region#clear">Clear</button>
 *   </div>
 */
export default class PolygonRegionController extends Controller {
  static targets = ["image", "svg", "input"]
  static values = {
    points: { type: Array, default: [] },
    closed: { type: Boolean, default: false },
  }

  // ---- Lifecycle ----

  connect() {
    this._draggingIndex = null
    this._dragOffset = { x: 0, y: 0 }
    this._setupSVG()
    this._render()
  }

  // ---- Values changed ----

  pointsValueChanged() {
    this._render()
    this._syncInput()
  }

  closedValueChanged() {
    this._render()
  }

  // ---- Actions ----

  /** Click on the SVG overlay — add a point or close the polygon. */
  addPoint(event) {
    if (this.closedValue) return // already closed — use drag instead

    const pt = this._eventToNormalised(event)
    if (!pt) return

    // If clicking near the first vertex and we have >=3 points → close.
    if (this.pointsValue.length >= 3) {
      const first = this.pointsValue[0]
      if (this._distance(pt, first) < 0.03) {
        this.closedValue = true
        return
      }
    }

    this.pointsValue = [...this.pointsValue, pt]
  }

  /** Close button (alternative to clicking the first vertex). */
  close() {
    if (this.pointsValue.length >= 3) {
      this.closedValue = true
    }
  }

  /** Clear all points and start over. */
  clear() {
    this.pointsValue = []
    this.closedValue = false
  }

  /** Mousedown on a vertex handle — start dragging. */
  startDrag(event) {
    event.preventDefault()
    event.stopPropagation()

    const index = parseInt(event.currentTarget.dataset.vertexIndex, 10)
    if (isNaN(index)) return

    this._draggingIndex = index
    const svg = this.svgTarget
    this._onMouseMove = this._handleDrag.bind(this)
    this._onMouseUp = this._endDrag.bind(this)
    svg.addEventListener("mousemove", this._onMouseMove)
    svg.addEventListener("mouseup", this._onMouseUp)
    document.addEventListener("mouseup", this._onMouseUp) // catch release outside SVG
  }

  /** Double-click on a vertex → remove it. */
  removeVertex(event) {
    event.preventDefault()
    event.stopPropagation()

    const index = parseInt(event.currentTarget.dataset.vertexIndex, 10)
    if (isNaN(index)) return
    if (this.pointsValue.length <= 3 && this.closedValue) {
      // Can't have a polygon with <3 vertices — reopen instead.
      this.closedValue = false
      return
    }

    const pts = [...this.pointsValue]
    pts.splice(index, 1)
    this.pointsValue = pts
  }

  /** Click on a midpoint marker → insert a new vertex between two existing ones. */
  insertVertex(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.closedValue) return // only available on closed polygons

    const afterIndex = parseInt(event.currentTarget.dataset.afterIndex, 10)
    if (isNaN(afterIndex)) return

    const a = this.pointsValue[afterIndex]
    const b = this.pointsValue[(afterIndex + 1) % this.pointsValue.length]
    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }

    const pts = [...this.pointsValue]
    pts.splice(afterIndex + 1, 0, mid)
    this.pointsValue = pts
  }

  // ---- Private ----

  _handleDrag(event) {
    if (this._draggingIndex === null) return

    const pt = this._eventToNormalised(event)
    if (!pt) return

    const pts = [...this.pointsValue]
    pts[this._draggingIndex] = {
      x: Math.max(0, Math.min(1, pt.x)),
      y: Math.max(0, Math.min(1, pt.y)),
    }
    this.pointsValue = pts
  }

  _endDrag() {
    this._draggingIndex = null
    if (this._onMouseMove) {
      this.svgTarget.removeEventListener("mousemove", this._onMouseMove)
      document.removeEventListener("mouseup", this._onMouseUp)
    }
  }

  _eventToNormalised(event) {
    const img = this.imageTarget
    const rect = img.getBoundingClientRect()
    const x = (event.clientX - rect.left) / rect.width
    const y = (event.clientY - rect.top) / rect.height
    if (x < 0 || x > 1 || y < 0 || y > 1) return null
    return { x: parseFloat(x.toFixed(4)), y: parseFloat(y.toFixed(4)) }
  }

  _distance(a, b) {
    return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2)
  }

  _setupSVG() {
    const svg = this.svgTarget
    svg.setAttribute("viewBox", "0 0 1 1")
    svg.setAttribute("preserveAspectRatio", "none")
    svg.style.position = "absolute"
    svg.style.inset = "0"
    svg.style.width = "100%"
    svg.style.height = "100%"
    svg.style.cursor = "crosshair"
  }

  _render() {
    const svg = this.svgTarget
    const pts = this.pointsValue
    const closed = this.closedValue

    // Clear existing SVG children.
    svg.innerHTML = ""

    if (pts.length === 0) return

    // Polygon fill (semi-transparent preview).
    if (pts.length >= 3) {
      const polyPoints = pts.map(p => `${p.x},${p.y}`).join(" ")
      const polygon = document.createElementNS("http://www.w3.org/2000/svg", "polygon")
      polygon.setAttribute("points", polyPoints)
      polygon.setAttribute("fill", closed ? "rgba(99, 102, 241, 0.20)" : "rgba(99, 102, 241, 0.10)")
      polygon.setAttribute("stroke", closed ? "#6366f1" : "#6366f1")
      polygon.setAttribute("stroke-width", "0.003")
      polygon.setAttribute("stroke-dasharray", closed ? "none" : "0.008 0.004")
      svg.appendChild(polygon)
    }

    // Lines connecting vertices.
    if (pts.length >= 2) {
      for (let i = 0; i < pts.length - 1; i++) {
        this._drawLine(svg, pts[i], pts[i + 1])
      }
      if (closed) {
        this._drawLine(svg, pts[pts.length - 1], pts[0])
      }
    }

    // Midpoint insert markers (only when closed).
    if (closed && pts.length >= 3) {
      for (let i = 0; i < pts.length; i++) {
        const a = pts[i]
        const b = pts[(i + 1) % pts.length]
        const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
        const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle")
        circle.setAttribute("cx", mid.x)
        circle.setAttribute("cy", mid.y)
        circle.setAttribute("r", "0.008")
        circle.setAttribute("fill", "rgba(99, 102, 241, 0.4)")
        circle.setAttribute("stroke", "#6366f1")
        circle.setAttribute("stroke-width", "0.002")
        circle.style.cursor = "pointer"
        circle.dataset.afterIndex = i
        circle.dataset.action = "click->polygon-region#insertVertex"
        svg.appendChild(circle)
      }
    }

    // Vertex handles.
    pts.forEach((p, i) => {
      const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle")
      circle.setAttribute("cx", p.x)
      circle.setAttribute("cy", p.y)
      circle.setAttribute("r", "0.012")
      circle.setAttribute("fill", i === 0 ? "#ef4444" : "#6366f1")
      circle.setAttribute("stroke", "white")
      circle.setAttribute("stroke-width", "0.003")
      circle.style.cursor = closed ? "grab" : "default"
      circle.dataset.vertexIndex = i
      circle.dataset.action = [
        "mousedown->polygon-region#startDrag",
        "dblclick->polygon-region#removeVertex",
      ].join(" ")
      svg.appendChild(circle)

      // Vertex number label.
      const text = document.createElementNS("http://www.w3.org/2000/svg", "text")
      text.setAttribute("x", p.x)
      text.setAttribute("y", p.y - 0.02)
      text.setAttribute("text-anchor", "middle")
      text.setAttribute("font-size", "0.018")
      text.setAttribute("fill", "white")
      text.setAttribute("stroke", "rgba(0,0,0,0.6)")
      text.setAttribute("stroke-width", "0.003")
      text.setAttribute("paint-order", "stroke")
      text.style.pointerEvents = "none"
      text.textContent = `${i + 1}`
      svg.appendChild(text)
    })
  }

  _drawLine(svg, a, b) {
    const line = document.createElementNS("http://www.w3.org/2000/svg", "line")
    line.setAttribute("x1", a.x)
    line.setAttribute("y1", a.y)
    line.setAttribute("x2", b.x)
    line.setAttribute("y2", b.y)
    line.setAttribute("stroke", "#6366f1")
    line.setAttribute("stroke-width", "0.002")
    line.style.pointerEvents = "none"
    svg.appendChild(line)
  }

  _syncInput() {
    if (!this.hasInputTarget) return

    const data = {
      type: "polygon",
      points: this.pointsValue,
    }
    this.inputTarget.value = JSON.stringify(data)
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }
}
